import { lazy, Suspense, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ScrollTrigger } from '@/lib/gsap';
import { useUIStore } from '@/store/uiStore';
import { useCartStore } from '@/store/cartStore';
import { useAuthStore } from '@/store/authStore';
import { Logo } from './Logo';
import { DesktopNav } from './DesktopNav';
import { AccountMenu } from './AccountMenu';
import { cn } from '@/lib/cn';

// CartPortal is a Three.js canvas. Lazy + SVG fallback keeps the three-vendor
// chunk out of the critical path — the chunk streams in after first paint.
const CartPortal = lazy(() =>
  import('@/components/three/CartPortal').then((m) => ({ default: m.CartPortal }))
);

interface CartIconFallbackProps {
  count?: number;
  size?: number;
  className?: string;
  onClick?: () => void;
}

function CartIconFallback({ count, size = 48, className, onClick }: CartIconFallbackProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Cart${count ? ` (${count} ${count === 1 ? 'item' : 'items'})` : ''}`}
      className={className}
      style={{ width: size, height: size }}
      data-magnetic
    >
      <span className="relative inline-flex h-6 w-6 items-center justify-center">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="9" cy="20" r="1.4" />
          <circle cx="17" cy="20" r="1.4" />
          <path d="M3 4h2l2.4 12.3a2 2 0 0 0 2 1.7h7.2a2 2 0 0 0 2-1.6L20 8H6" />
        </svg>
        {!!count && count > 0 && (
          <span
            aria-hidden
            className="absolute -right-2 -top-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[color:var(--color-brand-magenta)] px-1 text-[10px] font-bold text-white"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </span>
    </button>
  );
}

const SCROLL_THRESHOLD = 80; // PRD §8.1

interface IconButtonProps {
  label: string;
  onClick?: () => void;
  href?: string;
  children: React.ReactNode;
}

function IconButton({ label, onClick, href, children }: IconButtonProps) {
  const className =
    'inline-flex h-11 w-11 items-center justify-center rounded-full text-[color:var(--color-text-secondary)] transition-colors hover:text-white hover:bg-white/[0.06]';
  if (href) {
    return (
      <Link to={href} aria-label={label} className={className} data-magnetic>
        {children}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={className}
      data-magnetic
    >
      {children}
    </button>
  );
}

interface NavbarProps {
  className?: string;
}

/**
 * Top navigation (PRD §8.1).
 *
 * - Initial state: transparent + blur
 * - Scrolled state (>80px): bg-bg-deep/95 + bottom hairline glass
 * - GSAP ScrollTrigger drives the state class on the nav element + stores
 *   a flag in `uiStore.isNavScrolled` so other components can react.
 * - NavLinks animate a magenta underline from left on hover.
 * - Cart icon is the 3D `CartPortal` — bumps imperatively when cart count
 *   increases.
 */
export function Navbar({ className }: NavbarProps) {
  const navRef = useRef<HTMLElement | null>(null);

  const setNavScrolled = useUIStore((s) => s.setNavScrolled);
  const openMobileNav = useUIStore((s) => s.openMobileNav);

  const cartCount = useCartStore((s) => s.optimisticCount);
  const openCartDrawer = useCartStore((s) => s.openDrawer);

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();

  // Scroll-state via ScrollTrigger — single trigger flips the class once at 80px.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const apply = (scrolled: boolean) => {
      nav.dataset.scrolled = scrolled ? 'true' : 'false';
      setNavScrolled(scrolled);
    };

    const st = ScrollTrigger.create({
      start: `top top-=${SCROLL_THRESHOLD}`,
      onUpdate: (self) => apply(self.progress > 0),
      onEnter: () => apply(true),
      onEnterBack: () => apply(true),
      onLeaveBack: () => apply(false),
    });

    // Initial sync (in case page mounts already scrolled).
    apply(window.scrollY > SCROLL_THRESHOLD);

    return () => {
      st.kill();
    };
  }, [setNavScrolled]);

  return (
    <nav
      ref={navRef}
      className={cn(
        'fixed inset-x-0 top-0 z-[60] transition-[background-color,backdrop-filter,border-color] duration-300 ease-out',
        // Default state — transparent w/ subtle backdrop
        'border-b border-transparent',
        // Scrolled state via data attr (GSAP-friendly, no Tailwind variant churn)
        'data-[scrolled=true]:border-white/[0.06] data-[scrolled=true]:backdrop-blur-md',
        className
      )}
      data-scrolled="false"
      aria-label="Primary"
      style={{
        // Allow the data-scrolled attr to drive bg via CSS — keeps the CSS
        // co-located. Initial bg is transparent; scrolled is bg-deep/85.
        backgroundColor: 'var(--nav-bg, transparent)',
      }}
    >
      {/* Inline style hook for the scrolled bg colour */}
      <style>{`
        nav[data-scrolled="true"] { --nav-bg: rgba(5, 6, 26, 0.92); }
      `}</style>

      <div
        className="container-q flex h-[72px] items-center justify-between gap-4"
        style={{ paddingBlock: 0 }}
      >
        {/* Brand */}
        <Link
          to="/"
          aria-label="QuickXKart Home"
          className="flex items-center"
          data-magnetic
        >
          <Logo variant="compact" />
        </Link>

        {/* Desktop nav — data-driven mega-menu from the live category tree */}
        <DesktopNav />

        {/* Right cluster */}
        <div className="flex items-center gap-1">
          <IconButton label="Search" href="/search">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </IconButton>

          <IconButton label="Wishlist" href="/wishlist">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </IconButton>

          {/* Cart — 3D portal (lazy; SVG fallback used during chunk load) */}
          <Suspense
            fallback={
              <CartIconFallback
                count={cartCount}
                size={48}
                className="ml-1 inline-flex h-12 w-12 items-center justify-center rounded-full text-white transition-colors hover:bg-white/[0.06]"
                onClick={openCartDrawer}
              />
            }
          >
            <CartPortal
              count={cartCount}
              size={48}
              className="ml-1 inline-flex h-12 w-12 items-center justify-center rounded-full text-white transition-colors hover:bg-white/[0.06]"
              onClick={openCartDrawer}
            />
          </Suspense>

          {/* Account — dropdown with Orders / Wishlist / Profile / Sign Out */}
          {isAuthenticated ? (
            <AccountMenu />
          ) : (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="ml-1 hidden rounded-md border border-[color:var(--color-brand-magenta)]/60 px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-[color:var(--color-brand-magenta)]/10 md:inline-flex"
              data-magnetic
            >
              Login
            </button>
          )}

          {/* Mobile menu trigger */}
          <button
            type="button"
            onClick={openMobileNav}
            aria-label="Open menu"
            className="ml-1 inline-flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-white/[0.06] md:hidden"
            data-magnetic
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
        </div>
      </div>

    </nav>
  );
}
